<template>
  <div class="dashboard-container">
    <!-- 顶部标题栏 -->
    <div class="header">
      <div class="header-left">
        <div class="back-home-btn" @click="goBack">
          <span class="back-icon">←</span>
          <span class="back-text">返回主页</span>
        </div>
        <div class="project-date-group">
          <div class="search-dropdown" v-click-outside="closeDropdown">
            <input
              type="text"
              v-model="searchKeyword"
              placeholder="搜索项目..."
              class="search-input"
              @focus="showDropdown = true"
              @input="filterOptions"
            />
            <span class="dropdown-icon">▼</span>
            <div class="dropdown-list" v-show="showDropdown && filteredOptions.length > 0">
              <div
                class="dropdown-item"
                v-for="item in filteredOptions"
                :key="item.id"
                @click="selectProject(item)"
              >
                {{ item.name }}
              </div>
            </div>
          </div>
          <input
            type="date"
            v-model="selectedDate"
            @change="onDateChange"
            class="date-picker"
          />
        </div>
        <button class="export-btn" :disabled="exporting" @click="handleExport">
          {{ exporting ? '导出中...' : '导出 Excel' }}
        </button>
      </div>

      <div class="title">
        海康智慧电梯监测日管控
      </div>

      <div class="header-time">
        <span class="system-time">系统时间：{{ currentTime }}</span>
      </div>
    </div>

    <div class="page-content" :class="{ loading: loading }">
      <!-- 基本信息 -->
      <div class="panel-card">
        <div class="chart-title">电梯基本信息</div>
        <div class="info-grid-layout">
          <div class="info-item">
            <span class="info-label">项目地址</span>
            <span class="info-value">{{ basicInfo.address || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">注册代码</span>
            <span class="info-value">{{ basicInfo.registrationCode || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">电梯工号</span>
            <span class="info-value">{{ basicInfo.elevatorNumber || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">使用登记证</span>
            <span class="info-value">{{ basicInfo.usageCertificate || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">维保单位</span>
            <span class="info-value">{{ basicInfo.maintenanceUnit || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">安全管理员</span>
            <span class="info-value">{{ basicInfo.safetyAdmin || '-' }}</span>
          </div>
          <div class="info-item info-signature-cell">
            <span class="info-label">手写签名</span>
            <div class="info-value signature-box signature-box-tall">
              <img
                v-if="basicInfo.signature"
                :src="basicInfo.signature"
                alt="手写签名"
                class="signature-image"
                @error="onSignatureImageError"
              />
              <span v-else class="signature-placeholder">暂无签名</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 日管控结果汇总 -->
      <div class="panel-card summary-panel">
        <div class="summary-header">
          <div class="chart-title">日管控结果</div>
        </div>
        <div class="summary-selects">
          <div
            class="summary-item"
            v-for="field in summaryFields"
            :key="field.key"
          >
            <span class="summary-label">{{ field.label }}</span>
            <el-select
              v-model="formValues[field.key]"
              size="mini"
              popper-class="dark-select-dropdown"
              class="summary-el-select"
            >
              <el-option
                v-for="opt in field.options || statusOptions"
                :key="opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </div>
        </div>
      </div>

      <!-- 巡检项网格 -->
      <div class="checklist-grid">
        <div
          class="panel-card checklist-card"
          v-for="section in checklistLayout.mainSections"
          :key="section.key"
          :style="{ gridRow: section.gridRow, gridColumn: section.gridCol }"
        >
          <div class="chart-title">{{ section.title }}</div>
          <div class="checklist-items">
            <div
              class="checklist-row"
              v-for="item in section.items"
              :key="item.key"
            >
              <span class="item-label">{{ item.label }}</span>
              <div v-if="item.type === 'input'" class="inline-control">
                <input
                  v-model="formValues[item.key]"
                  class="form-input"
                  :placeholder="item.placeholder || ''"
                />
              </div>
              <div v-else class="custom-select inline-select">
                <select v-model="formValues[item.key]" class="form-select">
                  <option
                    v-for="opt in item.options"
                    :key="opt.value"
                    :value="opt.value"
                  >{{ opt.label }}</option>
                </select>
                <div class="select-arrow">▼</div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="checklistLayout.rightStackSections.length"
          class="panel-card checklist-card checklist-merged-right"
          :style="{
            gridRow: checklistLayout.rightStackPlacement.row,
            gridColumn: checklistLayout.rightStackPlacement.col
          }"
        >
          <div class="chart-title">{{ rightStackMergedTitle }}</div>
          <div class="checklist-items">
            <template v-for="section in checklistLayout.rightStackSections">
              <div
                class="checklist-row"
                v-for="item in section.items"
                :key="item.key"
              >
                <span class="item-label">{{ item.label }}</span>
                <div v-if="item.type === 'input'" class="inline-control">
                  <input
                    v-model="formValues[item.key]"
                    class="form-input"
                    :placeholder="item.placeholder || ''"
                  />
                </div>
                <div v-else class="custom-select inline-select">
                  <select v-model="formValues[item.key]" class="form-select">
                    <option
                      v-for="opt in item.options"
                      :key="opt.value"
                      :value="opt.value"
                    >{{ opt.label }}</option>
                  </select>
                  <div class="select-arrow">▼</div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- 底部：触发事件 & 工单记录 -->
      <div class="bottom-section">
        <div class="panel-card bottom-card events-panel">
          <div class="chart-title">今日触发事件</div>
          <DailyEventAnalysis
            :chart-data="eventChartData"
            :events="todayEvents"
            :year-stats="eventYearStats"
            empty-hide-stats
          />
        </div>

        <WorkOrderRecords
          :records="workOrderRecords"
          :container-style="{ flex: '1', minHeight: '200px', height: '100%' }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import WorkOrderRecords from '@/components/WorkOrderRecords.vue'
import DailyEventAnalysis from '@/components/DailyEventAnalysis.vue'
import { dashboardAPI } from '@/api/dashboard'
import { exportDailyControlReport } from '@/utils/exportExcel'
import {
  buildDailyEventChartData,
  buildDailyEventSummaryText,
  normalizeEventCounts,
  parseEventYearStats,
  parseTodayEventStat
} from '@/config/dailyEventConfig'
import {
  STATUS_OPTIONS,
  SUMMARY_STATUS_FIELDS,
  getChecklistSections,
  getChecklistGridLayout,
  createDefaultFormValues
} from '@/config/dailyControlConfig'

export default {
  name: 'DailyControlView',
  components: {
    WorkOrderRecords,
    DailyEventAnalysis
  },
  directives: {
    'click-outside': {
      bind(el, binding) {
        el._clickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el._clickOutside)
      },
      unbind(el) {
        document.removeEventListener('click', el._clickOutside)
      }
    }
  },
  data() {
    const today = this.formatDate(new Date())
    const sections = getChecklistSections('')
    return {
      loading: false,
      exporting: false,
      currentTime: '',
      timer: null,
      selectedDate: today,
      selectedProjectId: '',
      searchKeyword: '',
      showDropdown: false,
      filteredOptions: [],
      communityList: [],
      basicInfo: {
        address: '',
        registrationCode: '',
        elevatorNumber: '',
        usageCertificate: '',
        maintenanceUnit: '',
        safetyAdmin: '',
        signature: ''
      },
      formValues: createDefaultFormValues(sections),
      checklistSections: sections,
      summaryFields: SUMMARY_STATUS_FIELDS,
      statusOptions: STATUS_OPTIONS,
      todayEvents: [],
      allEvents: [],
      workOrderRecords: [],
      eventStr: '',
      eventCounts: {},
      eventYearStats: {
        thisYear: 0,
        lastYear: 0,
        total: 0
      }
    }
  },
  computed: {
    formattedReportDate() {
      if (!this.selectedDate) return ''
      const [y, m, d] = this.selectedDate.split('-')
      return `${y}年${m}月${d}日`
    },
    checklistLayout() {
      return getChecklistGridLayout(this.selectedProjectId)
    },
    eventChartData() {
      return buildDailyEventChartData(this.eventCounts)
    },
    rightStackMergedTitle() {
      return this.checklistLayout.rightStackSections.map((section) => section.title).join(' / ')
    }
  },
  created() {
    this.startClock()
    this.initPage()
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },

    formatDateTime(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      const h = String(date.getHours()).padStart(2, '0')
      const min = String(date.getMinutes()).padStart(2, '0')
      const s = String(date.getSeconds()).padStart(2, '0')
      return `${y}-${m}-${d} ${h}:${min}:${s}`
    },

    startClock() {
      this.currentTime = this.formatDateTime(new Date())
      this.timer = setInterval(() => {
        this.currentTime = this.formatDateTime(new Date())
      }, 1000)
    },

    goBack() {
      this.$router.push({ name: 'home' })
    },

    closeDropdown() {
      this.showDropdown = false
    },

    filterOptions() {
      const keyword = this.searchKeyword.toLowerCase()
      if (!keyword) {
        this.filteredOptions = [...this.communityList]
      } else {
        this.filteredOptions = this.communityList.filter((item) =>
          item.name.toLowerCase().includes(keyword)
        )
      }
    },

    selectProject(item) {
      this.searchKeyword = item.name
      this.selectedProjectId = String(item.id)
      this.showDropdown = false
      this.loadProjectData()
    },

    async initPage() {
      await this.loadCommunityList()
      this.filteredOptions = [...this.communityList]
      if (this.communityList.length > 0) {
        this.selectedProjectId = String(this.communityList[0].id)
        this.searchKeyword = this.communityList[0].name
        await this.loadProjectData()
      }
    },

    async loadCommunityList() {
      try {
        const cached = localStorage.getItem('communityData')
        if (cached) {
          this.communityList = JSON.parse(cached)
          if (this.communityList.length) return
        }
        const today = new Date()
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
        const response = await dashboardAPI.getScreenData({
          starttime: this.formatDate(startOfMonth),
          endtime: this.formatDate(today)
        })
        const data = response.data || response
        if (data.community_data && Array.isArray(data.community_data)) {
          this.communityList = data.community_data.map((item) => ({
            id: item.id,
            name: item.name || ''
          }))
        }
      } catch (e) {
        console.error('加载项目列表失败', e)
      }
    },

    onDateChange() {
      this.loadProjectData()
    },

    async loadProjectData() {
      if (!this.selectedProjectId) return
      this.loading = true
      try {
        const response = await dashboardAPI.getScreenReportThree({
          id: this.selectedProjectId,
          date: this.selectedDate
        })
        const data = this.getReportData(response)

        this.checklistSections = getChecklistSections(this.selectedProjectId)
        const defaults = createDefaultFormValues(this.checklistSections)
        Object.keys(defaults).forEach((key) => {
          this.$set(this.formValues, key, defaults[key])
        })

        const current = (data.community_data || []).find(
          (item) => String(item.id) === String(this.selectedProjectId)
        ) || {}

        const wbData = data.wb_data || {}
        const communityInfo = data.community_info || current
        this.basicInfo = {
          address: communityInfo.name || current.name || wbData.communityName || '',
          registrationCode: communityInfo.device_code || current.device_code || '',
          elevatorNumber: communityInfo.elevator_number || current.elevator_number || '',
          usageCertificate:
            communityInfo.elevator_sign || current.elevator_sign || wbData.elevator_sign || '',
          maintenanceUnit: communityInfo.elevator_unit || current.elevator_unit || '',
          safetyAdmin: '',
          signature: ''
        }

        const hasTodayEventStat =
          data.today_event_stat != null && data.today_event_stat !== undefined
        const todayEventStat = parseTodayEventStat(
          hasTodayEventStat ? data.today_event_stat : null
        )

        this.eventCounts = hasTodayEventStat
          ? todayEventStat.counts
          : normalizeEventCounts(data.eventCounts || data.event_counts)

        this.eventYearStats = parseEventYearStats(data.history_stat, data)
        if (todayEventStat.yearStats) {
          const merged = { ...this.eventYearStats }
          if (todayEventStat.yearStats.thisYear > 0) merged.thisYear = todayEventStat.yearStats.thisYear
          if (todayEventStat.yearStats.lastYear > 0) merged.lastYear = todayEventStat.yearStats.lastYear
          if (todayEventStat.yearStats.total > 0) merged.total = todayEventStat.yearStats.total
          this.eventYearStats = merged
        }

        this.eventStr = buildDailyEventSummaryText(this.eventCounts)

        if (hasTodayEventStat) {
          this.allEvents = todayEventStat.todayEvents
          this.todayEvents = todayEventStat.todayEvents
        } else if (data.event_data && Array.isArray(data.event_data)) {
          this.allEvents = data.event_data.map((item) => ({
            createtime: item.createtime || '',
            event_str: item.event_str || item.event_text || '',
            event_name: item.event_name || '',
            event_code: item.event || item.event_code,
            community_name: item.community_name || '',
            picture_file: item.picture_file || item.img || ''
          }))
          this.filterTodayData()
        } else {
          this.allEvents = []
          this.todayEvents = []
        }

        const orderList = this.parseInspectionOrderList(data)
        this.workOrderRecords = orderList.map((item) => this.mapWorkOrderRecord(item))

        this.applySafeStaff(data)
      } catch (e) {
        console.error('加载项目数据失败', e)
        this.$message && this.$message.error('加载项目数据失败')
      } finally {
        this.loading = false
      }
    },

    getReportData(response) {
      if (!response) return {}
      const root = response.data !== undefined ? response.data : response
      if (root && typeof root === 'object' && root.data && typeof root.data === 'object') {
        return root.data
      }
      return root || {}
    },

    parseInspectionOrderList(data) {
      const source = data?.inspection_order
      if (!source) return []
      if (Array.isArray(source)) return source.filter((item) => item && typeof item === 'object')
      if (typeof source === 'object') return [source]
      return []
    },

    mapWorkOrderRecord(item) {
      return {
        id: item.id ?? '',
        areaId: item.area_id ?? '',
        areaSiteId: item.area_site_id ?? '',
        code: item.code ?? '',
        createTime: item.createtime ?? item.create_time ?? item.createTime ?? '',
        completeTime: item.completetime ?? '',
        eventId: item.eventid ?? '',
        urgency: item.level_text ?? item.level ?? '',
        orderType: item.order_type_text ?? item.order_type ?? '',
        repairContent: item.repair_content ?? '',
        repairImage: item.repair_images ?? '',
        staffId: item.staff_id ?? '',
        staffImage: item.staff_image ?? '',
        responsibleEmployee: item.repair_staff_id ?? '',
        status: item.status_text ?? item.status ?? '',
        title: item.title ?? '',
        orderImage: item.images ?? '',
        reportEmployee: item.staff_id ?? '',
        orderEvent: item.content ?? '',
        communityAddress: item.area_id ?? ''
      }
    },

    isOrderOnDate(timeStr, dateStr) {
      if (!timeStr) return true
      const normalized = String(timeStr).trim()
      if (normalized.startsWith(dateStr)) return true
      const slashDate = dateStr.replace(/-/g, '/')
      if (normalized.startsWith(slashDate)) return true
      if (/^\d{10}$/.test(normalized)) {
        return this.formatDate(new Date(Number(normalized) * 1000)) === dateStr
      }
      if (/^\d{13}$/.test(normalized)) {
        return this.formatDate(new Date(Number(normalized))) === dateStr
      }
      const parsed = new Date(normalized.replace(/-/g, '/'))
      if (!Number.isNaN(parsed.getTime())) {
        return this.formatDate(parsed) === dateStr
      }
      return false
    },

    filterTodayData() {
      const dateStr = this.selectedDate
      this.todayEvents = this.allEvents.filter((item) => {
        if (!item.createtime) return true
        return this.isOrderOnDate(item.createtime, dateStr)
      })
    },

    applySafeStaff(data) {
      const safeStaff = data?.safe_staff
      const staff = Array.isArray(safeStaff) ? safeStaff[0] : safeStaff

      if (!staff || typeof staff !== 'object') {
        this.$set(this.basicInfo, 'safetyAdmin', '')
        this.$set(this.basicInfo, 'signature', '')
        return
      }

      const safetyAdmin =
        staff.staff_name ||
        staff.name ||
        staff.staff_id ||
        staff.safe_staff_id ||
        ''
      const staffImage =
        staff.image ||
        staff.staff_image ||
        staff.safe_staff_image ||
        ''

      this.$set(this.basicInfo, 'safetyAdmin', safetyAdmin)
      this.$set(
        this.basicInfo,
        'signature',
        staffImage ? String(staffImage).split(',')[0].trim() : ''
      )
    },

    formatEventDisplay(evt) {
      if (evt.event_str) return evt.event_str
      if (evt.event_name) return evt.event_name
      return '-'
    },

    onSignatureImageError() {
      this.$set(this.basicInfo, 'signature', '')
    },

    async handleExport() {
      this.exporting = true
      try {
        const filename = `海康智慧电梯监测日管控_${this.basicInfo.address || '项目'}_${this.selectedDate}.xlsx`
        await exportDailyControlReport({
          formattedReportDate: this.formattedReportDate,
          currentTime: this.currentTime,
          basicInfo: this.basicInfo,
          formValues: this.formValues,
          summaryFields: this.summaryFields,
          checklistSections: this.checklistSections,
          todayEvents: this.todayEvents,
          eventStr: this.eventStr,
          workOrderRecords: this.workOrderRecords,
          formatEventDisplay: (evt) => this.formatEventDisplay(evt),
          filename
        })
        this.$message && this.$message.success('导出成功')
      } catch (e) {
        console.error('导出失败', e)
        this.$message && this.$message.error('导出失败')
      } finally {
        this.exporting = false
      }
    }
  }
}
</script>

<style scoped lang="less">
.dashboard-container {
  background: linear-gradient(135deg, #0a1929 0%, #1a2332 50%, #0e2a42 100%);
  color: white;
  font-family: "Microsoft YaHei", sans-serif;
  min-height: 100vh;
  padding: 15px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  text-align: left;
}

.dashboard-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(circle at 20% 20%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(0, 212, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
  z-index: 1;
}

.dashboard-container::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: repeating-linear-gradient(90deg, transparent, transparent 98px, rgba(0, 212, 255, 0.03) 100px),
    repeating-linear-gradient(0deg, transparent, transparent 98px, rgba(0, 212, 255, 0.03) 100px);
  pointer-events: none;
  z-index: 1;
}

.dashboard-container > * {
  position: relative;
}

.header-time {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 4px 8px 4px 24px;
  grid-column: 3;
  justify-self: end;
  z-index: 1;
}

.system-time {
  font-size: 13px;
  color: #88c4ff;
  white-space: nowrap;
}

.header {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  margin-bottom: 20px;
  padding: 18px 28px 18px 20px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.1), transparent);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  gap: 15px;
}

.header::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.2), transparent);
  animation: headerScan 3s infinite;
}

@keyframes headerScan {
  0% { left: -100%; }
  100% { left: 100%; }
}

.header-left {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
  flex-wrap: wrap;
  grid-column: 1;
  justify-self: start;
  z-index: 1;
}

.back-home-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #00d4ff;
  font-size: 12px;
  white-space: nowrap;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
    transform: translateX(-2px);
  }
}

.back-icon {
  font-size: 14px;
  font-weight: bold;
  color: #00d4ff;
}

.back-text {
  font-size: 12px;
  color: #00d4ff;
}

.header-left {
  .project-date-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .date-picker {
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 5px;
    padding: 8px 10px;
    color: #ffffff;
    font-size: 12px;
    outline: none;
    transition: all 0.3s;
    min-width: 130px;
    box-sizing: border-box;
    height: 34px;

    &:hover,
    &:focus {
      border-color: #00d4ff;
      box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
    }

    &::-webkit-calendar-picker-indicator {
      filter: invert(1);
      cursor: pointer;
    }
  }

  .search-dropdown {
    position: relative;
    display: flex;
    align-items: center;
    background: rgba(0, 212, 255, 0.1);
    border: 1px solid rgba(0, 212, 255, 0.3);
    border-radius: 5px;
    padding: 8px;
    box-sizing: border-box;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      border-color: #00d4ff;
      box-shadow: 0 0 10px rgba(0, 212, 255, 0.3);
    }

    .search-input {
      background: transparent;
      border: none;
      outline: none;
      color: #fff;
      font-size: 12px;
      width: 200px;

      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }
    }

    .dropdown-icon {
      font-size: 10px;
      color: #00d4ff;
      margin-left: 5px;
    }

    .dropdown-list {
      position: absolute;
      top: 100%;
      left: 0;
      min-width: 300px;
      margin-top: 5px;
      background: rgba(10, 25, 41, 0.98);
      border: 1px solid rgba(0, 212, 255, 0.5);
      border-radius: 5px;
      max-height: 300px;
      overflow-y: auto;
      z-index: 9999;
      box-shadow: 0 4px 15px rgba(0, 212, 255, 0.3);

      .dropdown-item {
        padding: 10px 12px;
        font-size: 12px;
        color: #fff;
        cursor: pointer;
        transition: all 0.2s;
        border-bottom: 1px solid rgba(0, 212, 255, 0.1);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &:last-child { border-bottom: none; }

        &:hover {
          background: rgba(0, 212, 255, 0.2);
          color: #00d4ff;
        }
      }
    }
  }
}

.title {
  font-size: 22px;
  font-weight: bold;
  color: #00d4ff;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.6);
  letter-spacing: 1px;
  position: relative;
  white-space: nowrap;
  flex-shrink: 0;
  grid-column: 2;
  justify-self: center;
  text-align: center;
  z-index: 1;
}

.title::after {
  content: "";
  position: absolute;
  bottom: -5px;
  left: 50%;
  transform: translateX(-50%);
  width: 80%;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
}

.report-date {
  font-size: 16px;
  color: #88c4ff;
  font-weight: normal;
}

.export-btn {
  background: linear-gradient(135deg, rgba(0, 136, 204, 0.8), rgba(0, 68, 102, 0.8));
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 5px;
  padding: 6px 14px;
  color: white;
  cursor: pointer;
  font-size: 12px;
  white-space: nowrap;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00d4ff, #0088cc);
    box-shadow: 0 4px 15px rgba(0, 212, 255, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.page-content {
  display: flex;
  flex-direction: column;
  gap: 15px;

  &.loading {
    opacity: 0.6;
    pointer-events: none;
  }
}

.panel-card {
  background: rgba(10, 25, 41, 0.8);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 0 20px rgba(0, 212, 255, 0.2);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, rgba(0, 212, 255, 0.8), transparent);
  }
}

.chart-title {
  font-size: 16px;
  margin-bottom: 12px;
  color: #00d4ff;
  text-align: center;
  font-weight: bold;
}

.info-grid-layout {
  display: grid;
  grid-template-columns: repeat(3, 1fr) minmax(160px, 1fr);
  grid-template-rows: 1fr 1fr;
  gap: 12px;
  align-items: stretch;
}

.info-signature-cell {
  grid-column: 4;
  grid-row: 1 / span 2;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  align-self: stretch;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  align-self: stretch;
}

.info-label {
  font-size: 12px;
  color: #88c4ff;
}

.info-value {
  font-size: 13px;
  color: #fff;
  padding: 8px 10px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 136, 204, 0.1));
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 5px;
  border-left: 3px solid #00d4ff;
  min-height: 36px;
  box-sizing: border-box;
}

.signature-box {
  display: flex;
  align-items: center;
  justify-content: center;
}

.signature-box-tall {
  flex: 1;
  min-height: 0;
}

.signature-placeholder {
  color: #88c4ff;
  font-size: 12px;
}

.signature-image {
  max-width: 100%;
  max-height: 60px;
  object-fit: contain;
}

.summary-panel {
  .summary-header {
    margin-bottom: 18px;

    .chart-title {
      margin-bottom: 0;
      text-align: center;
    }
  }

  .summary-selects {
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    width: 100%;
    padding: 4px 60px 8px;
    box-sizing: border-box;
  }

  .summary-item {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .summary-label {
    font-size: 12px;
    color: #88c4ff;
    line-height: 1.4;
    white-space: nowrap;
  }

  .summary-el-select {
    width: 88px;

    /deep/ .el-input__inner {
      height: 28px;
      line-height: 28px;
      padding: 0 24px 0 8px;
      font-size: 12px;
      background: rgba(0, 212, 255, 0.15);
      border: none;
      color: #00d4ff;
    }

    /deep/ .el-input__icon {
      line-height: 28px;
      color: #00d4ff;
    }

    /deep/ .el-input.is-focus .el-input__inner,
    /deep/ .el-input__inner:hover {
      border: none;
      background: rgba(0, 212, 255, 0.25);
    }
  }
}

.custom-select {
  position: relative;
  display: inline-block;
}

.form-select {
  appearance: none;
  -webkit-appearance: none;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 5px;
  color: #00d4ff;
  padding: 5px 25px 5px 10px;
  font-size: 12px;
  min-width: 80px;
  cursor: pointer;
  outline: none;
  transition: all 0.3s ease;

  &:hover,
  &:focus {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
    box-shadow: 0 0 8px rgba(0, 212, 255, 0.3);
  }

  option {
    background: #0a1929;
    color: #fff;
  }
}

.select-arrow {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  color: #00d4ff;
  font-size: 10px;
  pointer-events: none;
}

.checklist-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 15px;
  align-items: stretch;
  --checklist-row-height: 38px;
  --checklist-title-height: 40px;
}

.checklist-card {
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.checklist-grid .chart-title {
  min-height: var(--checklist-title-height);
  margin-bottom: 0;
  line-height: 28px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.checklist-merged-right {
  padding: 15px;
  display: flex;
  flex-direction: column;
}

.checklist-items {
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: flex-start;
  gap: 0;
  min-height: 0;
}

.checklist-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 88px;
  align-items: center;
  gap: 10px;
  min-height: var(--checklist-row-height);
  height: var(--checklist-row-height);
  padding: 0;
  box-sizing: border-box;
  border-bottom: 1px solid rgba(0, 212, 255, 0.08);
  flex-shrink: 0;

  &:last-child { border-bottom: none; }
}

.item-label {
  font-size: 12px;
  color: #88c4ff;
  line-height: 1.4;
  min-width: 0;
}

.inline-control,
.inline-select {
  flex-shrink: 0;
  width: 88px;
  justify-self: end;
}

.inline-select .form-select {
  width: 100%;
  min-width: unset;
  height: 28px;
  box-sizing: border-box;
  padding: 4px 22px 4px 8px;
}

.form-input {
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.5);
  border-radius: 5px;
  color: #00d4ff;
  padding: 4px 8px;
  font-size: 12px;
  width: 100%;
  height: 28px;
  box-sizing: border-box;
  outline: none;
  transition: all 0.3s;

  &:hover,
  &:focus {
    background: rgba(0, 212, 255, 0.2);
    border-color: #00d4ff;
    box-shadow: 0 0 8px rgba(0, 212, 255, 0.3);
  }

  &::placeholder {
    color: rgba(136, 196, 255, 0.5);
    font-size: 11px;
  }
}

.bottom-section {
  display: flex;
  gap: 20px;
  align-items: stretch;

  > * {
    flex: 1;
    min-width: 0;
  }
}

.bottom-card {
  flex: 1;
}

.events-panel {
  min-height: 260px;
}

.data-table {
  margin-bottom: 12px;
}

.table-header {
  display: flex;
  background: rgba(0, 212, 255, 0.15);
  border-radius: 5px 5px 0 0;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.3);
}

.header-item {
  flex: 1;
  font-size: 12px;
  color: #00d4ff;
  font-weight: bold;
  text-align: center;

  &.flex-2 { flex: 2; }
}

.table-body {
  max-height: 160px;
  overflow-y: auto;
}

.table-row {
  display: flex;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.1);
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 212, 255, 0.08);
  }

  &:last-child { border-bottom: none; }
}

.table-cell {
  flex: 1;
  font-size: 12px;
  color: #fff;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &.flex-2 { flex: 2; }

  &.event-str-cell {
    white-space: normal;
    text-align: left;
    line-height: 1.5;
    word-break: break-all;
  }
}

.event-str-box {
  min-height: 80px;
  max-height: 160px;
  overflow-y: auto;
  padding: 12px 14px;
  font-size: 12px;
  color: #e8f4ff;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
  background: rgba(0, 212, 255, 0.05);
  border-radius: 5px;
  text-align: left;
}

.empty-tip {
  text-align: center;
  color: #88c4ff;
  padding: 24px;
  font-size: 14px;
}

@media (max-width: 1200px) {
  .checklist-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .checklist-card,
  .checklist-stack,
  .checklist-merged-right {
    grid-row: auto !important;
    grid-column: auto !important;
  }
  .info-grid-layout {
    grid-template-columns: repeat(2, 1fr);
  }
  .info-signature-cell {
    grid-column: auto;
    grid-row: auto;
  }
}

@media (max-width: 768px) {
  .header {
    grid-template-columns: 1fr;
    padding: 16px 20px;
    gap: 12px;
  }
  .header-left {
    grid-column: 1;
    justify-self: stretch;
  }
  .title {
    grid-column: 1;
    justify-self: center;
  }
  .header-time {
    grid-column: 1;
    justify-self: center;
    padding: 0;
  }
  .checklist-grid,
  .bottom-section,
  .info-grid-layout { grid-template-columns: 1fr; }
  .checklist-card,
  .checklist-stack,
  .checklist-merged-right {
    grid-row: auto !important;
    grid-column: auto !important;
  }
  .info-signature-cell {
    grid-column: auto;
    grid-row: auto;
  }
  .summary-panel .summary-selects {
    flex-direction: column;
    align-items: center;
    padding: 4px 20px 8px;
    gap: 12px;
  }
}
</style>

<style lang="less">
.dark-select-dropdown {
  background: rgba(10, 25, 41, 0.98) !important;
  border: 1px solid rgba(0, 212, 255, 0.4) !important;
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.25) !important;

  .el-select-dropdown__item {
    color: #e8f4ff;
    font-size: 12px;
    background: transparent;

    &:hover,
    &.hover {
      background: rgba(0, 212, 255, 0.2);
      color: #00d4ff;
    }

    &.selected {
      color: #00d4ff;
      font-weight: bold;
      background: rgba(0, 212, 255, 0.15);
    }
  }

  .popper__arrow::after {
    border-bottom-color: rgba(10, 25, 41, 0.98) !important;
  }
}
</style>
